import type { VariantProps } from 'class-variance-authority';
import { createContext, useContext, useRef, useState } from 'react';
import { Button, buttonVariants } from './ui/Button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/Dialog';
import { Spinner } from './ui/Spinner';

type ButtonVariant = NonNullable<
  VariantProps<typeof buttonVariants>['variant']
>;

type ConfirmModalOptions = {
  title?: string;
  message: string | React.ReactNode;
  confirmText?: string;
  cancelText?: string;
  confirmVariant?: ButtonVariant;
  cancelVariant?: ButtonVariant;
  showConfirm?: boolean;
  closeOnInteractOutside?: boolean;
};

type ConfirmModalContextType = {
  openModal: (
    options: ConfirmModalOptions,
    onConfirm?: () => void | Promise<void>,
    onCancel?: () => void | Promise<void>,
  ) => void;
};

const ConfirmModalProviderContext = createContext<
  ConfirmModalContextType | undefined
>(undefined);

export function ConfirmModalProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const [open, setOpen] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [options, setOptions] = useState<ConfirmModalOptions>({
    message: '',
    title: 'Confirm',
    confirmText: 'Confirm',
    cancelText: 'Cancel',
    confirmVariant: 'default',
    cancelVariant: 'outline',
    showConfirm: true,
    closeOnInteractOutside: true,
  });

  const onConfirmRef = useRef<(() => void | Promise<void>) | null>(null);
  const onCancelRef = useRef<(() => void | Promise<void>) | null>(null);

  const openModal = (
    modalOptions: ConfirmModalOptions,
    onConfirm?: () => void | Promise<void>,
    onCancel?: () => void | Promise<void>,
  ) => {
    setOptions({
      title: modalOptions.title ?? 'Confirm',
      confirmText: modalOptions.confirmText ?? 'Confirm',
      cancelText: modalOptions.cancelText ?? 'Cancel',
      confirmVariant: modalOptions.confirmVariant ?? 'default',
      cancelVariant: modalOptions.cancelVariant ?? 'outline',
      showConfirm: modalOptions.showConfirm ?? true,
      closeOnInteractOutside: modalOptions.closeOnInteractOutside ?? true,
      message: modalOptions.message,
    });
    onConfirmRef.current = onConfirm ?? null;
    onCancelRef.current = onCancel ?? null;
    setOpen(true);
  };

  const closeModal = () => {
    setOpen(false);
    setIsLoading(false);
    onConfirmRef.current = null;
    onCancelRef.current = null;
  };

  const handleConfirm = async () => {
    if (!onConfirmRef.current) {
      closeModal();
      return;
    }

    try {
      setIsLoading(true);
      await Promise.resolve(onConfirmRef.current());
      closeModal();
    } catch (error) {
      console.error('Confirmation callback failed:', error);
      setIsLoading(false);
    }
  };

  const handleCancel = async () => {
    if (!onCancelRef.current) {
      closeModal();
      return;
    }

    try {
      setIsLoading(true);
      await Promise.resolve(onCancelRef.current());
      closeModal();
    } catch (error) {
      console.error('Cancel callback failed:', error);
      setIsLoading(false);
    }
  };

  return (
    <ConfirmModalProviderContext.Provider value={{ openModal }}>
      {children}

      <Dialog
        open={open}
        onOpenChange={(isOpen) => {
          // This is triggered when clicking outside the dialog or close button is pressed.
          // But not triggered when we programmatically close the dialog via closeModal()
          if (!isOpen) {
            void handleCancel();
          }
        }}
      >
        <DialogContent
          className="sm:max-w-[425px]"
          onInteractOutside={(event) => {
            if (!options.closeOnInteractOutside) {
              event.preventDefault();
            }
          }}
          onEscapeKeyDown={(event) => {
            if (!options.closeOnInteractOutside) {
              event.preventDefault();
            }
          }}
        >
          <DialogHeader>
            <DialogTitle>{options.title}</DialogTitle>
            <DialogDescription />
          </DialogHeader>
          <div className="py-4">{options.message}</div>
          <DialogFooter>
            {options.showConfirm && (
              <Button
                onClick={() => {
                  void handleConfirm();
                }}
                variant={options.confirmVariant}
                disabled={isLoading}
              >
                {isLoading ? (
                  <Spinner className="tex-muted h-3" />
                ) : (
                  options.confirmText
                )}
              </Button>
            )}
            <Button
              onClick={() => {
                void handleCancel();
              }}
              variant={options.cancelVariant}
              disabled={isLoading}
            >
              {options.cancelText}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </ConfirmModalProviderContext.Provider>
  );
}

export const useConfirmModal = () => {
  const context = useContext(ConfirmModalProviderContext);

  if (context === undefined) {
    throw new Error(
      'useConfirmModal must be used within a ConfirmModalProvider',
    );
  }

  return context;
};
